// Loading State Management Service
// Provides centralized loading state management with user feedback

import React from 'react';

export interface LoadingState {
  isLoading: boolean;
  operation?: string;
  message?: string;
  progress?: number;
  startTime?: number;
  estimatedDuration?: number;
}

export interface LoadingOptions {
  operation: string;
  message?: string;
  estimatedDuration?: number;
  showProgress?: boolean;
}

export class LoadingManager {
  private static instance: LoadingManager;
  private loadingStates: Map<string, LoadingState> = new Map();
  private listeners: Map<string, Set<(state: LoadingState) => void>> = new Map();

  private constructor() {}

  public static getInstance(): LoadingManager {
    if (!LoadingManager.instance) {
      LoadingManager.instance = new LoadingManager();
    }
    return LoadingManager.instance;
  }

  /**
   * Start loading for a specific operation
   */
  public startLoading(key: string, options: LoadingOptions): void {
    const state: LoadingState = {
      isLoading: true,
      operation: options.operation,
      message: options.message || this.getDefaultMessage(options.operation),
      progress: options.showProgress ? 0 : undefined,
      startTime: Date.now(),
      estimatedDuration: options.estimatedDuration
    };

    this.loadingStates.set(key, state);
    this.notifyListeners(key, state);

    // Auto-update progress for operations with estimated duration
    if (options.estimatedDuration && options.showProgress) {
      this.startProgressSimulation(key, options.estimatedDuration);
    }
  }

  /**
   * Update loading progress
   */
  public updateProgress(key: string, progress: number, message?: string): void {
    const state = this.loadingStates.get(key);
    if (!state || !state.isLoading) return;

    const updatedState: LoadingState = {
      ...state,
      progress: Math.min(Math.max(progress, 0), 100),
      message: message || state.message
    };

    this.loadingStates.set(key, updatedState);
    this.notifyListeners(key, updatedState);
  }

  /**
   * Stop loading for a specific operation
   */
  public stopLoading(key: string): void {
    const state = this.loadingStates.get(key);
    if (!state) return;

    const updatedState: LoadingState = {
      ...state,
      isLoading: false,
      progress: 100
    };

    this.loadingStates.set(key, updatedState);
    this.notifyListeners(key, updatedState);

    // Clean up after a short delay to allow UI transitions
    setTimeout(() => {
      this.loadingStates.delete(key);
    }, 500);
  }

  /**
   * Get current loading state for a key
   */
  public getLoadingState(key: string): LoadingState | null {
    return this.loadingStates.get(key) || null;
  }

  /**
   * Check if any operation is loading
   */
  public isAnyLoading(): boolean {
    return Array.from(this.loadingStates.values()).some(state => state.isLoading);
  }

  /**
   * Get all current loading operations
   */
  public getAllLoadingOperations(): { key: string; state: LoadingState }[] {
    return Array.from(this.loadingStates.entries())
      .filter(([_, state]) => state.isLoading)
      .map(([key, state]) => ({ key, state }));
  }

  /**
   * Subscribe to loading state changes for a specific key
   */
  public subscribe(key: string, callback: (state: LoadingState) => void): () => void {
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }
    
    this.listeners.get(key)!.add(callback);

    // Return unsubscribe function
    return () => {
      const keyListeners = this.listeners.get(key);
      if (keyListeners) {
        keyListeners.delete(callback);
        if (keyListeners.size === 0) {
          this.listeners.delete(key);
        }
      }
    };
  }

  /**
   * Clear all loading states (useful for cleanup)
   */
  public clearAll(): void {
    this.loadingStates.clear();
    this.listeners.clear();
  }

  private notifyListeners(key: string, state: LoadingState): void {
    const keyListeners = this.listeners.get(key);
    if (keyListeners) {
      keyListeners.forEach(callback => callback(state));
    }
  }

  private startProgressSimulation(key: string, estimatedDuration: number): void {
    const startTime = Date.now();
    const interval = 100; // Update every 100ms

    const updateProgress = () => {
      const state = this.loadingStates.get(key);
      if (!state || !state.isLoading) return;

      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / estimatedDuration) * 90, 90); // Cap at 90% until completion

      this.updateProgress(key, progress);

      if (progress < 90) {
        setTimeout(updateProgress, interval);
      }
    };

    setTimeout(updateProgress, interval);
  }

  private getDefaultMessage(operation: string): string {
    const messages: Record<string, string> = {
      'fetch_nodes': 'Loading warehouse nodes...',
      'create_node': 'Creating warehouse node...',
      'update_node': 'Updating warehouse node...',
      'delete_node': 'Deleting warehouse node...',
      'fetch_inventory': 'Loading inventory data...',
      'update_inventory': 'Updating inventory...',
      'fetch_demand': 'Loading demand data...',
      'update_demand': 'Updating demand...',
      'fetch_gaps': 'Calculating demand gaps...',
      'run_optimization': 'Running optimization...',
      'save_data': 'Saving data...',
      'load_data': 'Loading data...'
    };

    return messages[operation] || 'Processing...';
  }
}

// Export singleton instance
export const loadingManager = LoadingManager.getInstance();

// Helper functions for common operations
export const startLoading = (key: string, options: LoadingOptions) => 
  loadingManager.startLoading(key, options);

export const stopLoading = (key: string) => 
  loadingManager.stopLoading(key);

export const updateProgress = (key: string, progress: number, message?: string) => 
  loadingManager.updateProgress(key, progress, message);

export const getLoadingState = (key: string) => 
  loadingManager.getLoadingState(key);

export const isAnyLoading = () => 
  loadingManager.isAnyLoading();

// React hook for loading state (to be used in components)
export const useLoadingState = (key: string) => {
  const [state, setState] = React.useState<LoadingState | null>(
    loadingManager.getLoadingState(key)
  );

  React.useEffect(() => {
    const unsubscribe = loadingManager.subscribe(key, setState);
    return unsubscribe;
  }, [key]);

  return state;
};